/**
 * Построение плоского массива виртуальных элементов для @tanstack/react-virtual.
 *
 * Виртуализация чата осложнена:
 * - Разделители дат (date-separator) между сообщениями за разные дни
 * - Разделитель непрочитанных (unread-separator)
 * - Группировка сообщений одного автора (isGrouped / isGroupedLast)
 * - Подсветка результатов поиска
 *
 * Все эти элементы превращаются в плоский список items,
 * над которым работает виртуализация.
 */

function getDayKey(date) {
    const d = new Date(date);
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

const GROUP_WINDOW_MS = 5 * 60 * 1000;

/**
 * @param {Object} params
 * @param {Array} params.messages — все сообщения чата, отсортированные по времени ASC
 * @param {string} [params.currentProfileId] — ID профиля текущего пользователя
 * @param {Function} params.isMessageRead — функция проверки `seenBy` для сообщения
 * @param {Array<string>} [params.searchResults] — ID сообщений, найденных поиском
 * @param {number} [params.currentSearchIndex] — индекс текущего результата в searchResults
 * @param {boolean} [params.searchMode] — активен ли режим поиска
 * @param {string} [params.searchQuery] — поисковый запрос
 * @returns {{ items: Array, messageIdToItemIndex: Object.<string, number> }}
 */
export function buildVirtualItems({
    messages,
    currentProfileId,
    isMessageRead,
    searchResults,
    currentSearchIndex,
    searchMode,
    searchQuery,
}) {
    const items = [];
    const messageIdToItemIndex = {};

    const firstUnreadIndex = messages.findIndex(msg => !isMessageRead(msg));
    const searchResultSet = new Set(searchResults || []);
    const isSearchActive = searchMode && searchQuery?.trim();

    // Строим Map<id, message> один раз для O(1) поиска reply-сообщений
    // вместо O(n) find() на каждое сообщение.
    const messagesById = new Map();
    messages.forEach(msg => messagesById.set(msg.id, msg));

    messages.forEach((message, index) => {
        const msgDate = message.createdAt || message.sentAt;
        const prevMsgDate = index > 0
            ? (messages[index - 1].createdAt || messages[index - 1].sentAt)
            : null;

        // === Разделитель даты ===
        const showDateSeparator = index === 0
            || getDayKey(msgDate) !== getDayKey(prevMsgDate);

        if (showDateSeparator && msgDate) {
            items.push({
                type: 'date-separator',
                key: `date-${getDayKey(msgDate)}`,
                date: msgDate,
            });
        }

        // === Разделитель непрочитанных ===
        if (index === firstUnreadIndex) {
            items.push({
                type: 'unread-separator',
                key: 'unread-sep',
            });
        }

        // === Sender info ===
        const senderData = typeof message.sender === 'object' ? message.sender : null;
        const senderId = senderData?.id || senderData?.userId || senderData?.profileId || message.senderId;
        const isOwnMessage = currentProfileId && senderId === currentProfileId;

        // === Группировка (isGrouped / isGroupedLast) ===
        const prevMsg = index > 0 ? messages[index - 1] : null;
        const prevSenderData = prevMsg
            ? (typeof prevMsg.sender === 'object' ? prevMsg.sender : null)
            : null;
        const prevSenderId = prevMsg
            ? (prevSenderData?.id || prevSenderData?.userId || prevSenderData?.profileId || prevMsg.senderId)
            : null;
        const prevDate = prevMsgDate ? new Date(prevMsgDate).getTime() : 0;
        const curDate = msgDate ? new Date(msgDate).getTime() : 0;
        const sameSender = prevMsg && senderId && prevSenderId && senderId === prevSenderId;
        const withinWindow = prevMsg && (curDate - prevDate) <= GROUP_WINDOW_MS;
        const breaksGroup = showDateSeparator
            || !!message.replyToMessageId
            || (prevMsg && !!prevMsg.replyToMessageId)
            || message.isDeleted
            || (prevMsg && prevMsg.isDeleted);
        const isGrouped = sameSender && withinWindow && !breaksGroup;

        const nextMsg = index < messages.length - 1 ? messages[index + 1] : null;
        const nextSenderData = nextMsg
            ? (typeof nextMsg.sender === 'object' ? nextMsg.sender : null)
            : null;
        const nextSenderId = nextMsg
            ? (nextSenderData?.id || nextSenderData?.userId || nextSenderData?.profileId || nextMsg.senderId)
            : null;
        const nextDate = nextMsg
            ? new Date(nextMsg.createdAt || nextMsg.sentAt).getTime()
            : 0;
        const nextWithinWindow = nextMsg && (nextDate - curDate) <= GROUP_WINDOW_MS;
        const nextBreaksGroup = nextMsg
            && (getDayKey(nextMsg.createdAt || nextMsg.sentAt) !== getDayKey(msgDate)
                || !!nextMsg.replyToMessageId
                || nextMsg.isDeleted);
        const isGroupedLast = isGrouped && (!nextMsg || nextSenderId !== senderId || !nextWithinWindow || nextBreaksGroup);

        // === Родительское сообщение для превью ответа ===
        const replyMsg = message.replyToMessageId
            ? messagesById.get(message.replyToMessageId)
            : null;

        // === CSS-класс поиска ===
        let searchClass = '';
        if (searchResults?.length > 0 && searchResults[currentSearchIndex] === message.id) {
            searchClass = 'chat-window__message-wrap--search-current';
        } else if (searchResultSet.has(message.id)) {
            searchClass = 'chat-window__message-wrap--search-match';
        } else if (isSearchActive) {
            searchClass = 'chat-window__message-wrap--search-dim';
        }

        const itemIndex = items.length;
        messageIdToItemIndex[message.id] = itemIndex;

        items.push({
            type: 'message',
            key: message.id,
            message,
            originalIndex: index,
            isOwn: isOwnMessage,
            isGrouped,
            isGroupedLast,
            replyMsg,
            searchClass,
        });
    });

    return { items, messageIdToItemIndex };
}